import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    getIsFuelSavingCardState,
    getVehicleTabs,
    getFuelEconomyData,
    fetchFuelEconomy,
    type FuelEconomyData,
    type VehicleTab
} from '@makemydeal/dr-activities-fuel-saving';
import * as tradeInsSelectors from '@makemydeal/dr-dash-store/selectors/tradeIns';
import { getBaggageFuelEconomy } from '@makemydeal/dr-dash-store/selectors/baggageFuelEconomySelectors';
import { mergeFuelEconomyFromApi } from '@makemydeal/dr-dash-store/actions/fuelEconomyActionCreators';
import { scopedActions, scopedStateSelectors } from '@makemydeal/shared-scoped-store';
import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import type { TradeInListItem, UseFuelEconomyResult, CurrentScopeSelectorState } from '../types';
import { getTradeInVins, buildFuelEconomyFetchPlan, buildTradeInFuelEconomyFromInit } from '../utils';

export const useFuelEconomy = (): UseFuelEconomyResult => {
    const [fuelEconomyData, setFuelEconomyData] = useState<FuelEconomyData | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    const dispatch = useDispatch();
    const isFuelSavingCardState = useSelector(getIsFuelSavingCardState);
    const vehicleTabs: VehicleTab[] = useSelector(getVehicleTabs);
    const fuelEconomyFromStore = useSelector(getFuelEconomyData);
    const currentScopeId = useSelector((state) => scopedStateSelectors.getCurrentScopeId(state as CurrentScopeSelectorState));

    const allTradeIns = useSelector(tradeInsSelectors.getAllTradeIns) as TradeInListItem[];
    const baggageFuelEconomy = useSelector(getBaggageFuelEconomy);

    // Keep refs so guards inside callbacks always read the latest values
    // without making them reactive dependencies of the callbacks.
    const baggageFuelEconomyRef = useRef(baggageFuelEconomy);
    baggageFuelEconomyRef.current = baggageFuelEconomy;

    const sellingVehiclesWithDataRef = useRef<ReturnType<typeof buildFuelEconomyFetchPlan>['sellingVehiclesWithData']>([]);
    const tradeInFuelEconomyFromInitRef = useRef<ReturnType<typeof buildTradeInFuelEconomyFromInit>>([]);

    // Prevents re-dispatching the RSAA call when downstream store updates
    // (mergeFuelEconomyFromApi changing vehicleTabs/allTradeIns) cause fetchData's
    // deps to change and the effect re-runs while the slideout is still open.
    // Reset on slideout close so the next open fetches fresh data.
    const hasFetchedRef = useRef(false);

    // React to store update after FETCH_FUEL_ECONOMY_SUCCESS / FETCH_FUEL_ECONOMY_FAILURE
    useEffect(() => {
        if (!fuelEconomyFromStore) return;

        setIsLoading(false);

        if (fuelEconomyFromStore.ok && fuelEconomyFromStore.data) {
            // Dispatch merge to dash-store (vehicle/trade reducers)
            const mergeAction = mergeFuelEconomyFromApi({
                sellingVehicles: fuelEconomyFromStore.data.sellingVehiclesVin || [],
                tradeInVehicles: fuelEconomyFromStore.data.tradeinVehiclesVin || []
            });
            if (typeof currentScopeId === 'string' && currentScopeId.length > 0) {
                dispatch(scopedActions.createScopedAction(currentScopeId, mergeAction) as AnyFSA);
            } else {
                dispatch(mergeAction as AnyFSA);
            }

            // Merge API response with seeded init data
            setFuelEconomyData({
                ok: true,
                data: {
                    sellingVehiclesVin: [
                        ...sellingVehiclesWithDataRef.current,
                        ...(fuelEconomyFromStore.data.sellingVehiclesVin || [])
                    ],
                    tradeinVehiclesVin: [
                        ...tradeInFuelEconomyFromInitRef.current,
                        ...(fuelEconomyFromStore.data.tradeinVehiclesVin || [])
                    ]
                }
            });
        }
    }, [fuelEconomyFromStore, currentScopeId, dispatch]);

    const fetchData = useCallback(async () => {
        if (hasFetchedRef.current) return;

        const baggage = baggageFuelEconomyRef.current;
        const tradeinVehiclesVin = getTradeInVins(allTradeIns);
        const safeVehicleTabs = vehicleTabs ?? [];

        // Only fetch if we have at least one VIN
        if (safeVehicleTabs.length === 0 && tradeinVehiclesVin.length === 0) {
            return;
        }

        // Check if we can use fuel economy data from init API for trade-ins
        const tradeInFuelEconomyFromInit = buildTradeInFuelEconomyFromInit(allTradeIns);
        const { sellingVehiclesWithData, sellingVinsForApi, tradeInVinsForApi, canUseExistingData } = buildFuelEconomyFetchPlan(
            safeVehicleTabs,
            tradeinVehiclesVin,
            tradeInFuelEconomyFromInit
        );

        if (canUseExistingData) {
            setFuelEconomyData({
                ok: true,
                data: {
                    sellingVehiclesVin: sellingVehiclesWithData,
                    tradeinVehiclesVin: tradeInFuelEconomyFromInit
                }
            });
            return;
        }

        // Seed with whatever data we already have so the form can prefill immediately
        if (sellingVehiclesWithData.length > 0 || tradeInFuelEconomyFromInit.length > 0) {
            setFuelEconomyData({
                ok: true,
                data: {
                    sellingVehiclesVin: sellingVehiclesWithData,
                    tradeinVehiclesVin: tradeInFuelEconomyFromInit
                }
            });
        }

        // Skip API call if we have all data
        if (sellingVinsForApi.length === 0 && tradeInVinsForApi.length === 0) {
            return;
        }

        // Skip API call for VINs where the user has already manually applied MPG values.
        const vinsNeedingApi = sellingVinsForApi.filter((vin) => !baggage?.vehicles?.[vin]);
        const tradeVinsNeedingApi = tradeInVinsForApi.filter((vin) => !baggage?.trades?.[vin]);
        if (vinsNeedingApi.length === 0 && tradeVinsNeedingApi.length === 0) {
            return;
        }

        // Capture seed data so the store-response useEffect can merge correctly
        sellingVehiclesWithDataRef.current = sellingVehiclesWithData;
        tradeInFuelEconomyFromInitRef.current = tradeInFuelEconomyFromInit;

        hasFetchedRef.current = true;
        setIsLoading(true);
        // Dispatch RSAA action directly — apiMiddleware handles the fetch,
        // FETCH_FUEL_ECONOMY_SUCCESS updates the store, useEffect above reacts to it.
        dispatch(
            fetchFuelEconomy({
                sellingVehiclesVin: vinsNeedingApi,
                tradeinVehiclesVin: tradeVinsNeedingApi
            }) as unknown as AnyFSA
        );
    }, [vehicleTabs, allTradeIns, dispatch]);

    // Reset data when slideout closes
    useEffect(() => {
        if (!isFuelSavingCardState) {
            // Reset on close so next open fetches fresh data
            hasFetchedRef.current = false;
            setFuelEconomyData(null);
        }
    }, [isFuelSavingCardState]);

    // Fetch when slideout opens (without re-fetching on every fetchData change)
    // Use a ref to track if we've already triggered the initial fetch to avoid
    // unnecessary API calls when Redux state updates cause fetchData to be recreated
    const hasInitialFetchTriggered = useRef(false);
    useEffect(() => {
        if (isFuelSavingCardState && !hasInitialFetchTriggered.current) {
            hasInitialFetchTriggered.current = true;
            fetchData();
        }
    }, [isFuelSavingCardState, fetchData]);

    // Reset the fetch trigger when slideout closes so next open will fetch again
    useEffect(() => {
        if (!isFuelSavingCardState) {
            hasInitialFetchTriggered.current = false;
        }
    }, [isFuelSavingCardState]);

    return {
        fuelEconomyData,
        isLoading,
        refetch: fetchData
    };
};
